/**
 * UploadVideoDialog — upload video a blocchi da 4MB (max 300MB) con barra di
 * avanzamento: init → N chunk sequenziali → complete (assemblaggio + storage).
 */
import React, { useState } from 'react';
import { trainingApi } from '../../lib/api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Progress } from '../ui/progress';
import { Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';

const CHUNK = 4 * 1024 * 1024;
const MAX_SIZE = 300 * 1024 * 1024;
const TYPE_BY_EXT = { mp4: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime' };

const videoType = (file) => {
  if (file.type && file.type !== 'application/octet-stream') return file.type;
  const ext = file.name.includes('.') ? file.name.split('.').pop().toLowerCase() : '';
  return TYPE_BY_EXT[ext] || file.type || '';
};

export default function UploadVideoDialog({ onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [phase, setPhase] = useState(null); // null | 'upload' | 'finalize'
  const [progress, setProgress] = useState(0);

  const pick = (f) => {
    if (!f) { setFile(null); return; }
    if (f.size > MAX_SIZE) { toast.error('Video troppo grande (max 300MB)'); return; }
    if (!Object.values(TYPE_BY_EXT).includes(videoType(f))) {
      toast.error('Formato non supportato (mp4, webm, mov)');
      return;
    }
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ''));
  };

  const upload = async () => {
    if (!file) { toast.error('Seleziona un video'); return; }
    if (!title.trim()) { toast.error('Inserisci un titolo'); return; }
    setPhase('upload');
    setProgress(0);
    try {
      const init = await trainingApi.videoInit({
        filename: file.name, title: title.trim(),
        description: description.trim() || null,
        size: file.size, content_type: videoType(file),
      });
      const uploadId = init.data.upload_id;
      for (let start = 0; start < file.size; start += CHUNK) {
        await trainingApi.videoChunk(uploadId, file.slice(start, start + CHUNK));
        setProgress(Math.round(Math.min(start + CHUNK, file.size) / file.size * 100));
      }
      setPhase('finalize');
      await trainingApi.videoComplete(uploadId);
      toast.success('Video caricato!');
      onUploaded();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Caricamento fallito, riprova');
      setPhase(null);
    }
  };

  const busy = phase !== null;

  return (
    <Dialog open onOpenChange={v => { if (!v && !busy) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="upload-video-dialog"
                     onInteractOutside={e => { if (busy) e.preventDefault(); }}
                     onEscapeKeyDown={e => { if (busy) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle>Carica video tutorial</DialogTitle>
          <DialogDescription>mp4, webm o mov — max 300MB. Non chiudere durante il caricamento.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Video *</label>
            <Input type="file" accept=".mp4,.webm,.mov,video/mp4,video/webm,video/quicktime"
                   disabled={busy}
                   onChange={e => pick(e.target.files?.[0] || null)}
                   className="rounded-none mt-1" data-testid="upload-video-input" />
            {file && <p className="text-[11px] text-slate-400 mt-1">{file.name} · {(file.size / 1048576).toFixed(1)} MB</p>}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Titolo *</label>
            <Input value={title} onChange={e => setTitle(e.target.value)} maxLength={160} disabled={busy}
                   placeholder="Es. Chiusura cassa — procedura completa"
                   className="rounded-none mt-1" data-testid="upload-video-title" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Descrizione</label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} maxLength={2000}
                      rows={2} disabled={busy} placeholder="Di cosa parla il video (opzionale)"
                      className="rounded-none mt-1 text-sm" data-testid="upload-video-description" />
          </div>
          {busy && (
            <div data-testid="upload-video-progress">
              <Progress value={phase === 'finalize' ? 100 : progress} className="h-2 rounded-none" />
              <p className="text-[11px] text-slate-500 mt-1">
                {phase === 'finalize' ? 'Finalizzazione in corso… (può richiedere fino a un minuto)' : `Caricamento ${progress}%`}
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Annulla</Button>
          <Button onClick={upload} disabled={busy || !file} data-testid="upload-video-confirm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Upload className="w-4 h-4 mr-1.5" />} Carica
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
